/**
 * Converts Shopify richtext JSON (root/paragraph/text) to HTML.
 * Handles plain strings and { type, children, value, bold, italic } structures.
 */
function richtextToHtml(val) {
  if (val == null) return '';
  if (typeof val === 'string') {
    const s = val.trim();
    if (!s.length) return '';
    if (s.startsWith('{')) {
      try {
        const parsed = JSON.parse(s);
        return richtextToHtml(parsed);
      } catch (_) {}
    }
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  if (typeof val !== 'object' || !val) return '';

  function escapeHtml(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function kids(n) {
    const c = n.children || n.content;
    return Array.isArray(c) ? c : [];
  }

  function run(node) {
    if (node == null) return '';
    if (typeof node === 'string') return escapeHtml(node);
    if (typeof node !== 'object') return '';

    switch (node.type) {
      case 'root':
        return kids(node).map(run).join('');
      case 'paragraph':
        return '<p>' + kids(node).map(run).join('') + '</p>';
      case 'text': {
        let t = escapeHtml(String(node.value ?? node.content ?? ''));
        if (node.bold) t = '<strong>' + t + '</strong>';
        if (node.italic) t = '<em>' + t + '</em>';
        return t;
      }
      case 'list':
        return '<ul>' + kids(node).map((c) => '<li>' + run(c) + '</li>').join('') + '</ul>';
      case 'list_item':
        return kids(node).map(run).join('');
      default: {
        const v = node.value ?? node.content;
        if (v != null && v !== '') return escapeHtml(String(v));
        return kids(node).map(run).join('');
      }
    }
  }

  return run(val);
}

function hasRichtextContent(val) {
  if (val == null) return false;
  if (typeof val === 'string') return val.trim().length > 0;
  if (typeof val !== 'object') return false;
  const k = val.children || val.content;
  if (Array.isArray(k) && k.length > 0) return true;
  if (val.value != null && val.value !== '') return true;
  if (typeof val.content === 'string' && val.content.trim().length > 0) return true;
  return false;
}

function metaFieldsToValues(fields) {
  const out = {};
  if (!Array.isArray(fields)) return out;
  fields.forEach((f) => {
    if (!f || typeof f.key !== 'string') return;
    out[f.key] = f.value;
  });
  return out;
}

/**
 * Obtiene el objeto de valores de un metaobjeto del API.
 * Soporta formato con array "fields" ({ key, type, value }) o propiedades en la raíz.
 */
function getMetaValues(obj) {
  if (!obj) return {};
  if (Array.isArray(obj.fields)) return metaFieldsToValues(obj.fields);
  return obj;
}

const CONECTOR_PASO_GRADUACION = -1;

/** Metaobjeto: boolean como true o string "true". */
function metaBoolTrue(v) {
  return v === true || v === 'true';
}

/* ==== Medición del configurador (GA4) ==== */
// ID de la propiedad GA4 a la que se envían los eventos del configurador.
// Si se deja vacío, gtag los envía a todos los destinos configurados en la página.
const FUNNEL_GA4_ID = 'G-8FZSG8XFKS';

function funnelTrack(eventName, params) {
  try {
    const payload = Object.assign({ page_path: window.location.pathname }, params || {});
    if (FUNNEL_GA4_ID) payload.send_to = FUNNEL_GA4_ID;
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    } else {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    }
    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(eventName, payload);
    }
  } catch (_) {
    /* La medición nunca debe romper el configurador */
  }
}

class CustomFunnel {
  constructor(containerEl, data) {
    const t = data?.translations || {};
    this.container = containerEl;
    this.funnelData = { titulo: data?.funnel?.titulo || null, pasos: [] };
    this.funnelName = data?.funnel?.titulo || null;
    this.root = containerEl?.closest?.('.custom-funnel') || null;
    this.pasoActual = null;
    this.pasosMap = {};
    this.historial = [];
    this.indiceHistorial = 0;
    this.selecciones = {};
    this.productData = data?.product ? { ...data.product } : null;
    this.urlSubidaGraduacion = (data?.urlSubidaGraduacion || 'https://gafas-de-optica-middleware.vercel.app/api/upload').trim();
    this.translations = {
      graduacion_titulo: t.graduacion_titulo || 'Graduación',
      graduacion_subir: t.graduacion_subir || 'Subir graduación en formato JPG/PNG',
      graduacion_ya_teneis: t.graduacion_ya_teneis || 'Ya tenéis mi graduación',
      graduacion_enviar_mas_tarde: t.graduacion_enviar_mas_tarde || 'Enviar más tarde',
      graduacion_imagen_ok: t.graduacion_imagen_ok || 'Imagen subida correctamente.',
      graduacion_subir_aria: t.graduacion_subir_aria || 'Subir graduación',
      graduacion_subiendo: t.graduacion_subiendo || 'Subiendo…',
      graduacion_url_no_configurada: t.graduacion_url_no_configurada || 'URL de subida no configurada.',
      graduacion_api_sin_url: t.graduacion_api_sin_url || 'La API no devolvió la URL de la imagen',
      graduacion_error_subida: t.graduacion_error_subida || 'Error al subir.',
      close: t.close || 'Cerrar',
      precio_gratis: t.precio_gratis || 'Gratis',
    };

    if (!this.container) {
      return;
    }
    this.hydrateFromApiAsync();
    this.iniciar();
  }
  
  /** Suma de los incrementos de precio de las opciones elegidas hasta ahora. */
  calcularTotalParcial() {
    let total = 0;
    Object.keys(this.selecciones || {}).forEach((k) => {
      if (String(k) === String(CONECTOR_PASO_GRADUACION)) return;
      const v = this.selecciones[k];
      const p = v && v.incrementoPrecio != null ? Number(v.incrementoPrecio) : 0;
      if (!isNaN(p)) total += p;
    });
    return Math.round(total * 100) / 100;
  }

  /** Datos comunes de cada evento: paso actual y total acumulado. */
  trackContext() {
    const paso = this.pasoActual;
    const esGraduacion = paso && paso.conectorPasos === CONECTOR_PASO_GRADUACION;
    return {
      funnel_name: this.funnelName || '',
      step_index: (this.indiceHistorial || 0) + 1,
      step_id: paso ? String(paso.conectorPasos) : '',
      step_name: paso ? (esGraduacion ? 'graduacion' : (paso.titulo || String(paso.conectorPasos))) : '',
      total_parcial: this.calcularTotalParcial(),
      currency: 'EUR',
    };
  }

  crearMapaPasos() {
    // Crear un mapa para acceder rápidamente a los pasos por su conectorPasos
    const mapa = {};
    this.funnelData.pasos.forEach((paso) => {
      // Usar conectorPasos como clave principal
      if (paso.conectorPasos !== undefined) {
        mapa[paso.conectorPasos] = paso;
      }
      // También usar el título como clave si existe
      if (paso.titulo) {
        mapa[paso.titulo] = paso;
      }
    });
    return mapa;
  }
  
  encontrarPasoPorDestino(pasoDestino) {
    // Buscar el paso que corresponde al pasoDestino usando conectorPasos
    const paso = this.pasosMap[pasoDestino];
    return paso;
  }
  
  iniciar() {
    // Los clics se manejan por delegación en document; no añadimos listeners aquí.
  }

  /** @returns {{ opcion: object, conectorPasos: number } | null} */
  obtenerSeleccionVarianteConPaso() {
    for (const key of Object.keys(this.selecciones)) {
      const conectorPasos = Number(key);
      if (conectorPasos === CONECTOR_PASO_GRADUACION) continue;
      const opcion = this.selecciones[key];
      if (opcion && opcion.variante) return { opcion, conectorPasos };
    }
    return null;
  }

  obtenerVarianteSeleccionada() {
    return this.obtenerSeleccionVarianteConPaso()?.opcion ?? null;
  }

  tieneOpcionConVariante() {
    return !!this.obtenerVarianteSeleccionada();
  }

  requierePasoGraduacion() {
    const sel = this.obtenerSeleccionVarianteConPaso();
    if (!sel) return false;
    if (sel.opcion.sinGraduar === true) return false;
    const paso = this.pasosMap[sel.conectorPasos];
    if (paso && metaBoolTrue(paso.ocultarAdjuntarGraduacion)) return false;
    return true;
  }

  graduacionCompletada() {
    const grad = this.selecciones[CONECTOR_PASO_GRADUACION];
    if (!grad) return false;
    if (grad.tipo === 'ya_teneis' || grad.tipo === 'enviar_mas_tarde') return true;
    if (grad.tipo === 'subir' && grad.url) return true;
    return false;
  }

  actualizarEstadoBotonComprar() {
    const btn = this.root?.querySelector('#btn-comprar-gafa-cristales') ?? document.getElementById('btn-comprar-gafa-cristales');
    if (!btn) return;
    const tieneVariante = this.tieneOpcionConVariante();
    const requiereGraduacion = this.requierePasoGraduacion();
    const puede = tieneVariante && (!requiereGraduacion || this.graduacionCompletada());
    btn.disabled = !puede;
  }

  hydrateFunnelFromApi(apiData) {
    if (!apiData) return;
    const selectedFunnel = Array.isArray(apiData.finalFunnel) && apiData.finalFunnel[0] ? apiData.finalFunnel[0] : null;
    if (!selectedFunnel) return;
    const rawPasosDelFunnel = selectedFunnel.pasos_del_funnel ?? selectedFunnel.pasos_del_funnel?.value;
    const pasosDelFunnel = Array.isArray(rawPasosDelFunnel) ? rawPasosDelFunnel : [];
    const funnelSteps = Array.isArray(apiData.funnelStep) ? apiData.funnelStep : [];
    const funnelStepById = {};
    funnelSteps.forEach((step) => {
      if (step && step.id) funnelStepById[step.id] = step;
    });

    const pasosByConnector = {};
    pasosDelFunnel.forEach((ref) => {
      const stepId = typeof ref === 'string' ? ref : ref?.id;
      if (!stepId) return;
      const stepMeta = funnelStepById[stepId];
      if (!stepMeta) return;
      const vals = getMetaValues(stepMeta);
      const refVals = typeof ref === 'object' && ref ? getMetaValues(ref) : {};
      const conector =
        vals.conector_pasos != null && vals.conector_pasos !== '' ? Number(vals.conector_pasos) : null;
      if (!conector) return;
      if (pasosByConnector[conector]) return;
      const pasoInicial =
        vals.paso_inicial === 'true' ||
        vals.paso_inicial === true ||
        refVals.paso_inicial === 'true' ||
        refVals.paso_inicial === true;
      const ocultarAdjuntarGraduacion =
        metaBoolTrue(vals.ocultar_adjuntar_graduacion) ||
        metaBoolTrue(refVals.ocultar_adjuntar_graduacion);
      pasosByConnector[conector] = {
        titulo: vals.titulo_del_paso_opcional || vals.titulo || null,
        conectorPasos: conector,
        formato: vals.formato || 'grid',
        pasoInicial,
        ocultarAdjuntarGraduacion,
        opciones: [],
      };
    });

    function getStepConnectorForOption(vals) {
      if (vals.nombre_opcion) {
        const m = String(vals.nombre_opcion).match(/^(\d+)\./);
        if (m) return Number(m[1]);
      }
      return null;
    }

    const funnelOptions = Array.isArray(apiData.funnelOption) ? apiData.funnelOption : [];
    funnelOptions.forEach((opt) => {
      const vals = getMetaValues(opt);
      const stepConnector = getStepConnectorForOption(vals);
      if (!stepConnector || !pasosByConnector[stepConnector]) return;
      let incrementoPrecio = 0;
      let incrementoPrecioFormato = 0;
      if (vals.incremento_del_precio) {
        try {
          const money = JSON.parse(vals.incremento_del_precio);
          const amount = parseFloat(money.amount || 0);
          if (!Number.isNaN(amount)) {
            incrementoPrecio = amount;
            incrementoPrecioFormato = Math.round(amount * 100);
          }
        } catch (_) {}
      }
      const pasoDestino =
        vals.paso_de_destino != null && vals.paso_de_destino !== '' ? Number(vals.paso_de_destino) : 0;

      let variante = null;
      if (vals.variante) {
        const gid = String(vals.variante);
        const m = gid.match(/(\d+)$/);
        if (m) {
          variante = m[1];
        }
      }
      const opcion = {
        nombre: vals.nombre || '',
        descripcion: vals.descripcion || '',
        informacionAdicional: vals.informacion_adicional || null,
        incrementoPrecio,
        incrementoPrecioFormato,
        pasoDestino,
        etiqueta: vals.etiqueta || null,
        resaltar: vals.resaltar === 'true' || vals.resaltar === true,
        icono: null,
        sinGraduar: vals.sin_graduar === 'true' || vals.sin_graduar === true,
        variante,
      };
      pasosByConnector[stepConnector].opciones.push(opcion);
    });

    const pasos = Object.values(pasosByConnector).sort(
      (a, b) => a.conectorPasos - b.conectorPasos,
    );
    const idxPasoInicial = pasos.findIndex((p) => p.pasoInicial);
    if (idxPasoInicial > 0) {
      const [startStep] = pasos.splice(idxPasoInicial, 1);
      pasos.unshift(startStep);
    }
    const funnelVals = getMetaValues(selectedFunnel);
    const titulo = funnelVals.titulo || null;
    this.funnelData = {
      titulo,
      pasos,
    };
  }

  hydrateFromApiAsync() {
    if (typeof fetchFunnels !== 'function') return;
    const name = this.funnelName || null;
    const p =
      typeof funnelsApiPromise !== 'undefined' &&
      funnelsApiPromise &&
      funnelsApiFunnelName === (name || null)
        ? funnelsApiPromise
        : fetchFunnels(name);
    p
      .then((apiData) => {
        this.hydrateFunnelFromApi(apiData);
        this.pasosMap = this.crearMapaPasos();
        this.pasosMap[CONECTOR_PASO_GRADUACION] = {
          conectorPasos: CONECTOR_PASO_GRADUACION,
          titulo: this.translations.graduacion_titulo,
          opciones: [
            { nombre: this.translations.graduacion_subir, pasoDestino: 0 },
            { nombre: this.translations.graduacion_ya_teneis, pasoDestino: 0 },
            { nombre: this.translations.graduacion_enviar_mas_tarde, pasoDestino: 0 },
          ],
        };
        this.actualizarEstadoBotonComprar();
      })
      .catch((err) => {
        console.error('CustomFunnel hydrateFromApiAsync:', err);
      });
  }

  getCartSectionIds() {
    const components = document.querySelectorAll('cart-items-component');
    const ids = [];
    components.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  /** Tras añadir al carrito con éxito: limpia selecciones, vuelve al primer paso y cierra la UI del funnel. */
  reiniciarFunnelTrasCarrito() {
    this.selecciones = {};
    const pasos = this.funnelData?.pasos;
    if (Array.isArray(pasos) && pasos.length > 0) {
      const primerPaso = pasos[0];
      this.pasoActual = primerPaso;
      this.historial = [primerPaso.conectorPasos];
      this.indiceHistorial = 0;
    } else {
      this.pasoActual = null;
      this.historial = [];
      this.indiceHistorial = 0;
    }
    if (this.container) {
      this.container.style.display = 'none';
      this.container.innerHTML = '';
    }
    const btnElegir =
      this.root?.querySelector('#btn-elegir-cristales') ?? document.getElementById('btn-elegir-cristales');
    const btnComprarGafa =
      this.root?.querySelector('#btn-comprar-gafa-cristales') ??
      document.getElementById('btn-comprar-gafa-cristales');
    if (btnElegir) btnElegir.style.display = '';
    if (btnComprarGafa) btnComprarGafa.style.display = 'none';
    this.actualizarEstadoBotonComprar();
  }

  resolverVariantIdSeleccionada() {
    const section = this.root?.closest?.('.shopify-section');
    const variantPicker = this.root?.querySelector?.('variant-picker') || section?.querySelector?.('variant-picker');
    const variantJson = variantPicker?.querySelector?.('script[type="application/json"]')?.textContent?.trim();
    if (variantJson) {
      try {
        const parsed = JSON.parse(variantJson);
        const variantId = parsed?.id ? String(parsed.id) : null;
        if (variantId) return variantId;
      } catch (_) {}
    }

    const selectors = [
      'product-form-component input[name="id"]',
      'form[action*="/cart/add"] input[name="id"]',
      'variant-picker[data-template-product-match="true"] + form input[name="id"]',
    ];
    for (const selector of selectors) {
      const scoped = this.root?.querySelector?.(selector);
      if (scoped?.value) return String(scoped.value);
      const inSection = section?.querySelector?.(selector);
      if (inSection?.value) return String(inSection.value);
    }
    const productIdActual = variantPicker?.dataset?.productId || this.productData?.productId || null;
    if (
      lastFunnelVariantUpdate.variantId &&
      (!productIdActual || !lastFunnelVariantUpdate.productId || String(lastFunnelVariantUpdate.productId) === String(productIdActual))
    ) {
      return String(lastFunnelVariantUpdate.variantId);
    }
    return null;
  }

  getFunnelProductData() {
    if (!this.productData) this.productData = {};
    const variantIdActual = this.resolverVariantIdSeleccionada();
    if (variantIdActual) this.productData.variantId = variantIdActual;
    const section = this.root?.closest?.('.shopify-section');
    const variantPicker = this.root?.querySelector?.('variant-picker') || section?.querySelector?.('variant-picker');
    const productIdActual = variantPicker?.dataset?.productId;
    if (productIdActual) this.productData.productId = String(productIdActual);
    return this.productData;
  }

  async agregarAlCarrito() {
    const data = this.getFunnelProductData();
    if (!data || !data.variantId) return;
    const formData = new FormData();
    formData.append('id', String(data.variantId));
    formData.append('quantity', '1');
    const sectionIds = this.getCartSectionIds();
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    try {
      const cartAddUrl = this.root?.dataset?.cartAddUrl || (typeof window.Theme !== 'undefined' && window.Theme?.routes?.cart_add_url) || '/cart/add.js';
      const res = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: formData,
      });
      const json = await res.json();
      if (json.status && json.message) {
        if (typeof window.showNotify === 'function') window.showNotify(json.message, 'error');
        else console.warn('Cart add error:', json.message);
        return;
      }
      this.dispatchCartUpdate(1, json.sections, data.productId, json.id || data.variantId);
      this.reiniciarFunnelTrasCarrito();
    } catch (e) {
      console.error('CustomFunnel agregarAlCarrito:', e);
    }
  }

  generarIdAleatorio() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let id = '';
    for (let i = 0; i < 12; i++) id += chars.charAt(Math.floor(Math.random() * chars.length));
    return id;
  }

  async agregarConfiguracionAlCarrito() {
    const data = this.getFunnelProductData();
    const opcion = this.obtenerVarianteSeleccionada();
    if (!data || !data.variantId || !opcion || !opcion.variante) return;
    const cartAddUrl = this.root?.dataset?.cartAddUrl || (typeof window.Theme !== 'undefined' && window.Theme?.routes?.cart_add_url) || '/cart/add.js';
    const idVinculacion = this.generarIdAleatorio();
    const precioDecimal = opcion.incrementoPrecio != null ? opcion.incrementoPrecio : 0;
    const precioStr = precioDecimal === 0
      ? (this.translations?.precio_gratis || 'Gratis')
      : (precioDecimal % 1 === 0 ? precioDecimal + '€' : precioDecimal.toFixed(2).replace('.', ',') + '€');
    const configLabel = `Configuración: ${opcion.nombre || ''} - ${precioStr}`;
    const sectionIds = this.getCartSectionIds();
    const appendSections = (fd) => {
      if (sectionIds.length) fd.append('sections', sectionIds.join(','));
    };
    try {
      const fd1 = new FormData();
      fd1.append('id', String(data.variantId));
      fd1.append('quantity', '1');
      fd1.append('properties[_ID Vinculación]', idVinculacion);
      const grad = this.selecciones[CONECTOR_PASO_GRADUACION];
      if (grad?.url) fd1.append('properties[_Imagen graduación]', grad.url);
      else if (grad?.tipo === 'ya_teneis') fd1.append('properties[Graduación]', this.translations.graduacion_ya_teneis);
      else if (grad?.tipo === 'enviar_mas_tarde') fd1.append('properties[Graduación]', this.translations.graduacion_enviar_mas_tarde);
      appendSections(fd1);
      const r1 = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: fd1,
      });
      const j1 = await r1.json();
      if (j1.status && j1.message) {
        if (typeof window.showNotify === 'function') window.showNotify(j1.message, 'error');
        else console.warn('Cart add error (gafa):', j1.message);
        funnelTrack('funnel_error', Object.assign(this.trackContext(), { error_type: 'cart_add_montura' }));
        return;
      }
      const fd2 = new FormData();
      fd2.append('id', String(opcion.variante));
      fd2.append('quantity', '1');
      fd2.append('properties[_Configuración]', configLabel);
      fd2.append('properties[_ID Vinculación]', idVinculacion);
      if (grad?.url) fd2.append('properties[_Imagen graduación]', grad.url);
      else if (grad?.tipo === 'ya_teneis') fd2.append('properties[Graduación]', this.translations.graduacion_ya_teneis);
      else if (grad?.tipo === 'enviar_mas_tarde') fd2.append('properties[Graduación]', this.translations.graduacion_enviar_mas_tarde);
      appendSections(fd2);
      const r2 = await fetch(cartAddUrl, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' },
        body: fd2,
      });
      const j2 = await r2.json();
      if (j2.status && j2.message) {
        if (typeof window.showNotify === 'function') window.showNotify(j2.message, 'error');
        else console.warn('Cart add error (cristales):', j2.message);
        funnelTrack('funnel_error', Object.assign(this.trackContext(), { error_type: 'cart_add_cristales' }));
        return;
      }
      funnelTrack('funnel_complete', Object.assign(this.trackContext(), {
        lens_option: opcion.nombre || '',
        graduacion: grad?.tipo || 'ninguna',
        steps_completed: this.historial.length,
        value: this.calcularTotalParcial(),
      }));
      this.dispatchCartUpdate(2, j2.sections, data.productId, opcion.variante);
      this.reiniciarFunnelTrasCarrito();
    } catch (e) {
      console.error('CustomFunnel agregarConfiguracionAlCarrito:', e);
    }
  }

  dispatchCartUpdate(itemCount, sections, productId, variantId) {
    const e = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: {},
        sourceId: 'custom-funnel',
        data: {
          source: 'product-form-component',
          itemCount: itemCount,
          productId: productId,
          variantId: variantId,
          sections: sections || {},
        },
      },
    });
    document.dispatchEvent(e);
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') drawer.open();
  }
  
  mostrarFunnel() {
    if (!this.funnelData || !Array.isArray(this.funnelData.pasos) || !this.funnelData.pasos.length) {
      if (typeof funnelsApiPromise !== 'undefined' && funnelsApiPromise) {
        // Esperar a que se resuelva la llamada a la API y reintentar una sola vez
        funnelsApiPromise
          .then((apiData) => {
            if (!this.funnelData || !Array.isArray(this.funnelData.pasos) || !this.funnelData.pasos.length) {
              this.hydrateFunnelFromApi(apiData);
              this.pasosMap = this.crearMapaPasos();
              this.pasosMap[CONECTOR_PASO_GRADUACION] = {
                conectorPasos: CONECTOR_PASO_GRADUACION,
                titulo: this.translations.graduacion_titulo,
                opciones: [
                  { nombre: this.translations.graduacion_subir, pasoDestino: 0 },
                  { nombre: this.translations.graduacion_ya_teneis, pasoDestino: 0 },
                  { nombre: this.translations.graduacion_enviar_mas_tarde, pasoDestino: 0 },
                ],
              };
            }
            if (this.funnelData && Array.isArray(this.funnelData.pasos) && this.funnelData.pasos.length) {
              // Llamada recursiva controlada: ahora sí hay datos
              this.mostrarFunnel();
            }
          })
          .catch((err) => {
            console.error('CustomFunnel mostrarFunnel wait api:', err);
          });
      }
      return;
    }
    const primerPaso = this.funnelData.pasos[0];
    this.pasoActual = primerPaso;
    this.historial = [primerPaso.conectorPasos];
    this.indiceHistorial = 0;

    this.container.style.display = 'block';

    this._lastStepViewKey = null;
    funnelTrack('funnel_start', { funnel_name: this.funnelName || '' });

    const btnElegirCristales = this.root?.querySelector('#btn-elegir-cristales') ?? document.getElementById('btn-elegir-cristales');
    const btnComprarGafaCristales = this.root?.querySelector('#btn-comprar-gafa-cristales') ?? document.getElementById('btn-comprar-gafa-cristales');
    if (btnElegirCristales) btnElegirCristales.style.display = 'none';
    if (btnComprarGafaCristales) btnComprarGafaCristales.style.display = 'block';

    this.renderizarPaso();
    this.actualizarEstadoBotonComprar();
  }
  
  obtenerPasoAnterior() {
    if (this.indiceHistorial <= 0) {
      return null;
    }
    // Retroceder el índice
    this.indiceHistorial--;
    const pasoAnteriorConector = this.historial[this.indiceHistorial];
    return this.pasosMap[pasoAnteriorConector];
  }
  
  obtenerOpcionSeleccionada(paso) {
    // Obtener la opción seleccionada para un paso
    const conectorPasos = paso.conectorPasos;
    const seleccion = this.selecciones[conectorPasos];
    if (!seleccion) return null;
    
    // Identificar por nombre (único por paso). Varias opciones pueden compartir pasoDestino.
    return paso.opciones.find(opt => opt.nombre === seleccion.nombre);
  }
  
  puedeIrAdelante() {
    // Puede ir adelante si hay una opción seleccionada en el paso actual
    const opcionSeleccionada = this.obtenerOpcionSeleccionada(this.pasoActual);
    return opcionSeleccionada && opcionSeleccionada.pasoDestino > 0;
  }
  
  renderizarPaso() {
    if (!this.pasoActual) {
      this.container.innerHTML = '<p>No hay paso disponible</p>';
      return;
    }

    const paso = this.pasoActual;

    const viewKey = `${this.indiceHistorial}|${paso.conectorPasos}`;
    if (this._lastStepViewKey !== viewKey) {
      this._lastStepViewKey = viewKey;
      funnelTrack('funnel_step_view', this.trackContext());
    }

    // Paso virtual graduación
    if (paso.conectorPasos === CONECTOR_PASO_GRADUACION) {
      const grad = this.selecciones[CONECTOR_PASO_GRADUACION];
      const puedeIrAtras = this.indiceHistorial > 0;
      const selSubir = grad?.tipo === 'subir';
      const selYa = grad?.tipo === 'ya_teneis';
      const selEnviarMasTarde = grad?.tipo === 'enviar_mas_tarde';
      const T = this.translations;
      let html = '<div class="funnel-step">';
      html += `<div class="funnel-navigation">
        <button class="funnel-nav-btn funnel-nav-back" ${puedeIrAtras ? '' : 'disabled'} onclick="${puedeIrAtras ? 'window.funnelInstance.irAtras()' : ''}">←</button>
        <span class="funnel-step-title-nav">${paso.titulo || T.graduacion_titulo}</span>
        <button class="funnel-nav-btn funnel-nav-forward" disabled>→</button>
      </div>`;
      html += '<div class="funnel-options funnel-options-fila">';
      html += `<div class="funnel-option ${selSubir ? 'funnel-option-selected' : ''}" data-paso-destino="0" data-index="0">
        <div class="funnel-option-content"><h3 class="funnel-option-name">${T.graduacion_subir}</h3></div>
      </div>`;
      html += `<div class="funnel-option ${selYa ? 'funnel-option-selected' : ''}" data-paso-destino="0" data-index="1">
        <div class="funnel-option-content"><h3 class="funnel-option-name">${T.graduacion_ya_teneis}</h3></div>
      </div>`;
      html += `<div class="funnel-option ${selEnviarMasTarde ? 'funnel-option-selected' : ''}" data-paso-destino="0" data-index="2">
        <div class="funnel-option-content"><h3 class="funnel-option-name">${T.graduacion_enviar_mas_tarde}</h3></div>
      </div>`;
      html += '</div>';
      if (selSubir) {
        if (grad.url) {
          html += `<p class="funnel-graduacion-ok">${T.graduacion_imagen_ok}</p>`;
        } else {
          html += `<div class="funnel-graduacion-upload"><input type="file" id="funnel-graduacion-file" class="funnel-graduacion-file-hidden" accept="image/jpeg,image/png,.jpg,.jpeg,.png" aria-label="${T.graduacion_subir_aria}"><span class="funnel-graduacion-upload-status"></span></div>`;
        }
      }
      html += `<div class="funnel-info-popup" id="funnel-info-popup" aria-hidden="true">
        <div class="funnel-info-popup-overlay"></div>
        <div class="funnel-info-popup-box">
          <button type="button" class="funnel-info-popup-close" aria-label="${T.close}">×</button>
          <div class="funnel-info-popup-content"></div>
        </div>
      </div>`;
      html += '</div>';
      this.container.innerHTML = html;
      this.attachEventListeners();
      if (selSubir && !grad.url) {
        this.attachGraduacionUpload();
        setTimeout(() => this.container.querySelector('#funnel-graduacion-file')?.click(), 0);
      }
      this.actualizarEstadoBotonComprar();
      return;
    }

    const opcionSeleccionada = this.obtenerOpcionSeleccionada(paso);
    const puedeIrAtras = this.indiceHistorial > 0;
    const puedeIrAdelante = this.puedeIrAdelante();

    let html = '<div class="funnel-step">';
    
    // Navegación con flechas y título
    html += `<div class="funnel-navigation">
      <button class="funnel-nav-btn funnel-nav-back" ${puedeIrAtras ? '' : 'disabled'} onclick="${puedeIrAtras ? 'window.funnelInstance.irAtras()' : ''}">
        ←
      </button>
      ${paso.titulo ? `<span class="funnel-step-title-nav">${paso.titulo}</span>` : ''}
      <button class="funnel-nav-btn funnel-nav-forward" ${puedeIrAdelante ? '' : 'disabled'} onclick="${puedeIrAdelante ? 'window.funnelInstance.irAdelante()' : ''}">
        →
      </button>
    </div>`;
    
    // Opciones del paso
    if (paso.opciones && paso.opciones.length > 0) {
      // Agregar clase según el formato
      const formatoClase = paso.formato ? `funnel-options-${paso.formato}` : 'funnel-options-fila';
      html += `<div class="funnel-options ${formatoClase}">`;
      
      paso.opciones.forEach((opcion, index) => {
        const estaSeleccionada = opcionSeleccionada && opcionSeleccionada.nombre === opcion.nombre;
        const claseSeleccionada = estaSeleccionada ? 'funnel-option-selected' : '';
        
        html += `<div class="funnel-option ${claseSeleccionada}" data-paso-destino="${opcion.pasoDestino}" data-index="${index}">`;

        const hasInfo = hasRichtextContent(opcion.informacionAdicional);
        const hasLabel = opcion.etiqueta && String(opcion.etiqueta).trim().length > 0;
        if (hasInfo || hasLabel) {
          html += `<div class="funnel-option-meta">`;
          if (hasInfo) {
            html += `<button type="button" class="funnel-option-info-btn" data-index="${index}" aria-label="Información adicional">i</button>`;
          }
          if (hasLabel) {
            html += `<span class="funnel-option-label">${opcion.etiqueta}</span>`;
          }
          html += `</div>`;
        }

        if (opcion.icono && opcion.icono.url) {
          html += `<div class="funnel-option-icon">
            <img src="${opcion.icono.url}" alt="${opcion.icono.alt || opcion.nombre}" />
          </div>`;
        }

        html += `<div class="funnel-option-content">
          <h3 class="funnel-option-name">${opcion.nombre}</h3>`;
        if (hasRichtextContent(opcion.descripcion)) {
          html += `<div class="funnel-option-description">${richtextToHtml(opcion.descripcion)}</div>`;
        }

        const precioDecimal = opcion.incrementoPrecio || 0;
        const precioFormateado = precioDecimal === 0
          ? (this.translations?.precio_gratis || 'Gratis')
          : (precioDecimal % 1 === 0 ? precioDecimal + '€' : precioDecimal.toFixed(2).replace('.', ',') + '€');
        const precioDisplay = precioDecimal === 0 ? precioFormateado : `+${precioFormateado}`;
        html += `<span class="funnel-option-price">${precioDisplay}</span>
        </div></div>`;
      });
      
      html += '</div>';
    }

    const closeLabel = this.translations?.close || 'Cerrar';
    html += `<div class="funnel-info-popup" id="funnel-info-popup" aria-hidden="true">
      <div class="funnel-info-popup-overlay"></div>
      <div class="funnel-info-popup-box">
        <button type="button" class="funnel-info-popup-close" aria-label="${closeLabel}">×</button>
        <div class="funnel-info-popup-content"></div>
      </div>
    </div>`;

    html += '</div>';

    this.container.innerHTML = html;
    this.attachEventListeners();
    this.actualizarEstadoBotonComprar();
  }
  
  attachEventListeners() {
    const opciones = this.container.querySelectorAll('.funnel-option');

    opciones.forEach((opcionEl) => {
      opcionEl.addEventListener('click', (e) => {
        if (e.target.closest('.funnel-option-info-btn')) return;
        const pasoDestino = parseInt(opcionEl.dataset.pasoDestino, 10);
        const opcionIndex = parseInt(opcionEl.dataset.index, 10);
        this.seleccionarOpcion(pasoDestino, opcionIndex);
      });
    });

    this.container.querySelectorAll('.funnel-option-info-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = parseInt(btn.dataset.index, 10);
        const opcion = this.pasoActual?.opciones?.[index];
        const infoHtml = richtextToHtml(opcion?.informacionAdicional);
        if (!infoHtml) return;
        const popup = this.container.querySelector('#funnel-info-popup');
        const content = popup?.querySelector('.funnel-info-popup-content');
        if (!popup || !content) return;
        content.innerHTML = infoHtml;
        popup.classList.add('is-open');
        popup.setAttribute('aria-hidden', 'false');
      });
    });

    const popup = this.container.querySelector('#funnel-info-popup');
    if (!popup) return;
    const overlay = popup.querySelector('.funnel-info-popup-overlay');
    const closeBtn = popup.querySelector('.funnel-info-popup-close');
    const close = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
    };
    overlay?.addEventListener('click', close);
    closeBtn?.addEventListener('click', close);
  }

  attachGraduacionUpload() {
    const input = this.container.querySelector('#funnel-graduacion-file');
    const status = this.container.querySelector('.funnel-graduacion-upload-status');
    if (!input) return;
    input.addEventListener('change', (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (!this.urlSubidaGraduacion) {
        if (status) status.textContent = this.translations.graduacion_url_no_configurada;
        return;
      }
      if (status) status.textContent = this.translations.graduacion_subiendo;
      const formData = new FormData();
      formData.append('file', file);
      fetch(this.urlSubidaGraduacion, {
        method: 'POST',
        body: formData,
      })
        .then(async (r) => {
          const json = await r.json().catch(() => ({}));
          if (!r.ok) {
            const msg = json.error || r.statusText || 'Error de subida';
            throw new Error(msg);
          }
          return json;
        })
        .then(async (json) => {
          const url = json.url;
          if (!url) throw new Error(this.translations.graduacion_api_sin_url);
          if (!this.selecciones[CONECTOR_PASO_GRADUACION]) this.selecciones[CONECTOR_PASO_GRADUACION] = { tipo: 'subir' };
          this.selecciones[CONECTOR_PASO_GRADUACION].url = url;
          funnelTrack('funnel_prescription_upload', Object.assign(this.trackContext(), { status: 'ok' }));
          this.renderizarPaso();
          this.actualizarEstadoBotonComprar();
          await this.agregarConfiguracionAlCarrito();
        })
        .catch((err) => {
          if (status) status.textContent = err.message || this.translations.graduacion_error_subida;
          funnelTrack('funnel_prescription_upload', Object.assign(this.trackContext(), { status: 'error' }));
          console.error('CustomFunnel upload graduación:', err);
        });
    });
  }

  seleccionarOpcion(pasoDestino, opcionIndex) {
    const opcionSeleccionada = this.pasoActual?.opciones?.[opcionIndex];

    // Paso virtual graduación: guardar opción, re-renderizar y si "Ya tenéis" o "Enviar más tarde" añadir al carrito
    if (this.pasoActual.conectorPasos === CONECTOR_PASO_GRADUACION) {
      const tiposGraduacion = ['subir', 'ya_teneis', 'enviar_mas_tarde'];
      funnelTrack('funnel_option_select', Object.assign(this.trackContext(), {
        option_name: tiposGraduacion[opcionIndex] || String(opcionIndex),
        price_delta: 0,
      }));
      if (opcionIndex === 0) {
        this.selecciones[CONECTOR_PASO_GRADUACION] = { tipo: 'subir' };
      } else {
        this.selecciones[CONECTOR_PASO_GRADUACION] = {
          tipo: opcionIndex === 1 ? 'ya_teneis' : 'enviar_mas_tarde',
        };
        this.renderizarPaso();
        this.actualizarEstadoBotonComprar();
        this.agregarConfiguracionAlCarrito();
        return;
      }
      this.renderizarPaso();
      this.actualizarEstadoBotonComprar();
      return;
    }

    // Limpiar selecciones posteriores y estado de graduación
    this.limpiarSeleccionesPosteriores(this.pasoActual.conectorPasos);
    delete this.selecciones[CONECTOR_PASO_GRADUACION];

    // Guardar la selección
    this.selecciones[this.pasoActual.conectorPasos] = opcionSeleccionada;

    funnelTrack('funnel_option_select', Object.assign(this.trackContext(), {
      option_name: opcionSeleccionada?.nombre || '',
      price_delta: Number(opcionSeleccionada?.incrementoPrecio || 0),
    }));

    const nuevoPaso = pasoDestino && this.encontrarPasoPorDestino(pasoDestino);

    // Opción con variante y sin_graduar false/null: graduación virtual o carrito directo
    if (!nuevoPaso && opcionSeleccionada?.variante && opcionSeleccionada.sinGraduar !== true) {
      if (metaBoolTrue(this.pasoActual?.ocultarAdjuntarGraduacion)) {
        this.renderizarPaso();
        this.actualizarEstadoBotonComprar();
        this.agregarConfiguracionAlCarrito();
        return;
      }
      this.navegarAPasoGraduacion();
      return;
    }

    if (!nuevoPaso) {
      this.renderizarPaso();
      return;
    }

    this.navegarAPaso(pasoDestino, true);
  }
  
  limpiarSeleccionesPosteriores(conectorPasosActual) {
    // Obtener el índice del paso actual en el historial
    const indiceActual = this.historial.indexOf(conectorPasosActual);
    
    // Eliminar selecciones de todos los pasos después del actual en el historial
    if (indiceActual >= 0) {
      for (let i = indiceActual + 1; i < this.historial.length; i++) {
        const conectorPasos = this.historial[i];
        delete this.selecciones[conectorPasos];
      }
    }
  }
  
  navegarAPasoGraduacion() {
    const pasoGraduacion = this.pasosMap[CONECTOR_PASO_GRADUACION];
    if (!pasoGraduacion) return;
    this.pasoActual = pasoGraduacion;
    this.historial = this.historial.slice(0, this.indiceHistorial + 1);
    this.historial.push(CONECTOR_PASO_GRADUACION);
    this.indiceHistorial = this.historial.length - 1;
    this.renderizarPaso();
  }

  navegarAPaso(pasoDestino, agregarAlHistorial = false) {
    // Encontrar el paso correspondiente
    const nuevoPaso = this.encontrarPasoPorDestino(pasoDestino);
    
    if (!nuevoPaso) {
      console.warn(`CustomFunnel: No se encontró el paso destino ${pasoDestino}`);
      return;
    }
    
    this.pasoActual = nuevoPaso;
    
    // Agregar al historial si es una navegación hacia adelante
    if (agregarAlHistorial) {
      // Si ya había avanzado desde aquí, eliminar los pasos futuros
      this.historial = this.historial.slice(0, this.indiceHistorial + 1);
      // Agregar el nuevo paso
      this.historial.push(nuevoPaso.conectorPasos);
      this.indiceHistorial = this.historial.length - 1;
    }
    
    this.renderizarPaso();
  }
  
  irAtras() {
    funnelTrack('funnel_back', this.trackContext());
    if (this.pasoActual?.conectorPasos === CONECTOR_PASO_GRADUACION) {
      delete this.selecciones[CONECTOR_PASO_GRADUACION];
    }
    const pasoAnterior = this.obtenerPasoAnterior();
    if (pasoAnterior) {
      this.pasoActual = pasoAnterior;
      this.renderizarPaso();
    }
  }
  
  irAdelante() {
    const opcionSeleccionada = this.obtenerOpcionSeleccionada(this.pasoActual);
    if (opcionSeleccionada && opcionSeleccionada.pasoDestino) {
      this.navegarAPaso(opcionSeleccionada.pasoDestino, true);
    }
  }
}

function getFunnelDataFromRoot(root) {
  const script = root?.querySelector('script.funnel-data-json');
  if (!script?.textContent?.trim()) return null;
  try {
    const raw = script.textContent.trim();
    const data = JSON.parse(raw);
    return data;
  } catch (e) {
    return null;
  }
}

function getOrCreateFunnelInstance(root) {
  const data = getFunnelDataFromRoot(root);
  if (!data) return null;

  const container = root.querySelector('#funnel_content');
  if (!container) return null;

  const reuse = window.funnelInstance &&
    window.funnelInstance.root === root &&
    document.contains(root);

  if (reuse) {
    if (!window.funnelInstance.productData) {
      window.funnelInstance.productData = data.product ? { ...data.product } : null;
    }
    return window.funnelInstance;
  }

  window.funnelInstance = new CustomFunnel(container, data);
  return window.funnelInstance;
}

function handleFunnelClick(e) {
  if (!e.target.closest?.('.custom-funnel')) return;
  const target = /** @type {HTMLElement} */ (e.target.closest?.('#btn-elegir-cristales, #btn-comprar, #btn-comprar-gafa-cristales'));
  if (!target) return;

  const root = target.closest('.custom-funnel');
  if (!root) return;

  const instance = getOrCreateFunnelInstance(root);
  if (!instance) return;

  if (target.id === 'btn-elegir-cristales') {
    instance.mostrarFunnel();
    return;
  }
  if (target.id === 'btn-comprar') {
    instance.agregarAlCarrito();
    return;
  }
  if (target.id === 'btn-comprar-gafa-cristales') {
    instance.agregarConfiguracionAlCarrito();
  }
}

function initFunnelDelegation() {
  if (document.documentElement.dataset.funnelDelegationInit === 'true') {
    return;
  }
  document.documentElement.dataset.funnelDelegationInit = 'true';
  document.addEventListener('click', handleFunnelClick);
}

const lastFunnelVariantUpdate = {
  variantId: null,
  productId: null,
  sourceId: null,
  variantName: null,
};

function handleFunnelVariantTrace(e) {
  const variantId = e?.detail?.resource?.id ? String(e.detail.resource.id) : null;
  const productId = e?.detail?.data?.newProduct?.id || e?.detail?.data?.productId || null;
  const sourceId = e?.detail?.sourceId || null;
  const resource = e?.detail?.resource || {};
  const variantName =
    resource.title ||
    resource.name ||
    [resource.option1, resource.option2, resource.option3].filter(Boolean).join(' / ') ||
    null;
  lastFunnelVariantUpdate.variantId = variantId;
  lastFunnelVariantUpdate.productId = productId ? String(productId) : null;
  lastFunnelVariantUpdate.sourceId = sourceId ? String(sourceId) : null;
  lastFunnelVariantUpdate.variantName = variantName || null;
}

function initFunnelVariantTrace() {
  if (document.documentElement.dataset.funnelVariantTraceInit === 'true') return;
  document.documentElement.dataset.funnelVariantTraceInit = 'true';
  document.addEventListener('variant:update', handleFunnelVariantTrace);
}

const FUNNELS_API_BASE_URL = 'https://gafas-de-optica-middleware.vercel.app';

/**
 * @typedef {Object} FunnelsResponse
 * @property {Array<any>} funnelOption
 * @property {Array<any>} funnelStep
 * @property {Array<any>} finalFunnel
 * @property {string|null|Date} lastUpdated
 */

/**
 * Llama al backend para obtener los datos de funnels.
 * @param {string} [funnelName] - Nombre del funnel (metaobjeto final_funnel.nombre) a filtrar.
 * @returns {Promise<FunnelsResponse>}
 */
async function fetchFunnels(funnelName) {
  const url = new URL(`${FUNNELS_API_BASE_URL}/api/funnels`);

  // Si se pasa un nombre de funnel, filtramos en el backend por ese nombre
  if (typeof funnelName === 'string' && funnelName.trim().length > 0) {
    url.searchParams.set('funnelName', funnelName.trim());
  }

  const res = await fetch(url.toString(), {
    method: 'GET',
  });

  if (!res.ok) {
    throw new Error(`Error obteniendo funnels (status ${res.status})`);
  }

  const json = await res.json();

  return {
    funnelOption: json.funnel_option || [],
    funnelStep: json.funnel_step || [],
    finalFunnel: json.final_funnel || [],
    // Si prefieres Date, descomenta la línea de Date y comenta la de string
    // lastUpdated: json.lastUpdated ? new Date(json.lastUpdated) : null,
    lastUpdated: json.lastUpdated || null,
  };
}

let funnelsApiPromise = null;
let funnelsApiFunnelName = null;

function ensureFunnelsPrefetch(funnelName) {
  // Si no nos pasan nombre, intentamos inferirlo del primer custom-funnel del DOM
  let name = typeof funnelName === 'string' ? funnelName : null;
  if (!name) {
    try {
      const script = document.querySelector('.custom-funnel script.funnel-data-json');
      const raw = script?.textContent?.trim();
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.funnel?.titulo) name = String(parsed.funnel.titulo);
      }
    } catch (e) {}
  }

  if (funnelsApiPromise && funnelsApiFunnelName === (name || null)) return;
  if (typeof fetchFunnels !== 'function') return;
  funnelsApiFunnelName = name || null;
  funnelsApiPromise = fetchFunnels(name)
    .then((data) => data)
    .catch((err) => {
      console.error('CustomFunnel prefetch error:', err);
      throw err;
    });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initFunnelDelegation();
    initFunnelVariantTrace();
    ensureFunnelsPrefetch();
  });
} else {
  initFunnelDelegation();
  initFunnelVariantTrace();
  ensureFunnelsPrefetch();
}
